import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Flex,
  useDisclosure,
  VStack,
  HStack,
  Icon,
  IconButton,
  Text,
  Input,
  Tooltip,
  Button,
  CheckboxGroup,
  Stack,
  Checkbox,
  Link,
  CloseButton,
  useColorModeValue,
  Textarea,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  DeleteIcon,
  AttachmentIcon,
  CheckIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileSignature, faDollarSign, faBookmark } from "@fortawesome/free-solid-svg-icons";
import { RiSparkling2Fill } from "react-icons/ri";
import { cqAPI, getBid } from "../../lib/Auth";
import creditCardModalManager from "../DialogManager/creditCardModalManager";
import { sendEvent } from "../../lib/GA";
import MessagesTemplatesModal from "./MessageTemplatesModals";
const _ = require("lodash");

// Looks like a single line until Enter; auto-grows; keeps focus (uncontrolled)
const AutoGrowUncontrolledTextarea = React.memo(function AutoGrowUncontrolledTextarea({
  name,
  initialValue = "",
  placeholder,
  onValueChange, // (val: string) => void
}) {
  const ref = React.useRef(null);

  const handleInput = (e) => {
    const el = e.currentTarget;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
    onValueChange?.(el.value);
  };

  React.useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = ref.current.scrollHeight + "px";
  }, []);

  return (
    <Textarea
      name={name}
      ref={ref}
      defaultValue={initialValue}
      placeholder={placeholder}
      onInput={handleInput}
      rows={1}
      resize="none"
      overflow="hidden"
      variant="outline"
      size="md"
    />
  );
});

function SendArea({
  isPatient,
  disableSendButton,
  userInfo,
  toast,
  setUserInfo,
  messageType,
  setMessageType,
  typing,
  colorMode,
  uid,
  isStripeConnected,
  setIsStripeConnected,
  setDisableSendButton,
  staff,
  draft,
  setDraft,
  isDraftBoxOpen,
  setIsDraftBoxOpen,
  isMobile,
}) {
  const [filteredStaff, setFilteredStaff] = useState([]);
  const fileInputRef = useRef(null);
  const [forms, setForms] = useState([]);
  const {
    isOpen: isFormModalOpen,
    onOpen: onFormModalOpen,
    onClose: onFormModalClose,
  } = useDisclosure();

  // Replacements modal
  const {
    isOpen: isReplacementsOpen,
    onOpen: onReplacementsOpen,
    onClose: onReplacementsClose,
  } = useDisclosure();

  const {
    isOpen: isMessageTemplatesOpen,
    onOpen: onMessageTemplatesOpen,
    onClose: onMessageTemplatesClose,
  } = useDisclosure();

  const [payRequest, setPayRequest] = useState({});
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [attachedPayRequest, setAttachedPayRequest] = useState({});
  const [selectedForm, setSelectedForm] = useState("");
  const [attachedForm, setAttachedForm] = useState(null);

  // Track replacement values
  const [replacementValues, setReplacementValues] = useState({}); // scratch in modal
  const [attachedFormReplacements, setAttachedFormReplacements] = useState(null); // persisted with the attached form

  const textareaRef = useRef(null);
  const [usedSuggestedDraft, setUsedSuggestedDraft] = useState(null);
  const [placeHolderVisible, setPlaceHolderVisible] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHAR_COUNT = 1600;
  const iconBGColor = useColorModeValue("#F9F9FB", "#2C2F40");

  // Translation toggle
  const [translateEnabled, setTranslateEnabled] = useState(false);
  const [translationLangCode, setTranslationLangCode] = useState("");

  const [mentionedStaff, setMentionedStaff] = useState([]);
  const [replyType, setReplyType] = useState(
    window.location.pathname.startsWith("/inbox") ? "reply" : "internalNote"
  );

  const detectStripeConnected = async () => {
    const response = await cqAPI.get("/settings/stripe");
    setIsStripeConnected(response?.data?.connected);
  };

  // Avoid race with contentEditable initial fill
  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const messageParam = searchParams.get("message");
    if (!messageParam) return;

    let preFilled = "";
    try {
      preFilled = decodeURIComponent(messageParam);
    } catch {
      // ignore
    }

    if (preFilled && textareaRef.current) {
      textareaRef.current.innerText = preFilled;
      setCharCount(preFilled.length);
      setPlaceHolderVisible(false);
      adjustTextareaHeight();
    }
  }, []);

  useEffect(() => {
    detectStripeConnected();
  }, []);

  const handlePaste = (event) => {
    event.preventDefault();
    const pastingText = event.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    const textNode = document.createTextNode(pastingText);
    range.deleteContents();
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
    const currentValue = textareaRef.current.innerText;
    setCharCount(currentValue.length);
  };

  const handlePlaceHolderClick = () => {
    textareaRef.current.focus();
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    }
  };

  const replaceTextWithTemplate = (bodyText) => {
    const el = textareaRef.current;
    if (!el) return;
    el.innerText = bodyText || "";

    setPlaceHolderVisible(false);
    setCharCount(el.innerText.length);
    adjustTextareaHeight();

    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    el.focus();
  };

  // Attach or open replacements modal
  const handleAttachForm = async () => {
    if (!selectedForm) {
      toast({
        title: "No form selected.",
        description: "Please select a form to attach.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    const form = forms.find((f) => f._id === selectedForm);
    if (!form) return;

    if (Array.isArray(form.replacements) && form.replacements.length > 0) {
      const defaults = form.replacements.reduce((acc, key) => {
        acc[key] = ""; // prefill from userInfo if desired
        return acc;
      }, {});
      setReplacementValues(defaults);
      setAttachedForm(form);
      onFormModalClose();
      onReplacementsOpen();
      return;
    }

    // No replacements -> attach immediately
    setAttachedForm(form);
    setAttachedFormReplacements(null);
    onFormModalClose();
  };

  const handleFocus = () => {
    setIsDraftBoxOpen(false);
    setPlaceHolderVisible(false);
  };

  const setCursorAfterHtmlInsert = (contentEle) => {
    const range = document.createRange();
    const selection = window.getSelection();
    range.setStart(contentEle, contentEle.childNodes.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const debouncedTyping = _.debounce(async () => {
    await cqAPI.post("/chat/typing", {
      bid: getBid(),
      target_uid: userInfo.user_id,
      is_internal_note: replyType === "internalNote",
    });
  }, 3000);

  const insertStaffNameAtCursor = (member) => {
    setMentionedStaff([...mentionedStaff, member]);
    const currentValue = textareaRef.current.innerHTML;
    const lastAtPos = currentValue.lastIndexOf("@");
    const beforeAt = currentValue.substring(0, lastAtPos);
    textareaRef.current.innerHTML = `${beforeAt}<b>${member.profile.name}</b>&nbsp;`;
    setCursorAfterHtmlInsert(textareaRef.current);
    setFilteredStaff([]);
  };

  const handleBlur = () => {
    if (textareaRef.current.innerText) return;
    setPlaceHolderVisible(true);
  };

  useEffect(() => {
    setIsDraftBoxOpen(!!draft);
    if (!!draft) {
      setUsedSuggestedDraft("loaded");
    }
  }, [draft]);

  const handleDraftCloseClick = async () => {
    setIsDraftBoxOpen(false);
    setUsedSuggestedDraft("ignored");
  };

  const handleDraftBoxClick = async () => {
    if (!!draft) {
      setUsedSuggestedDraft("editing");
      textareaRef.current.innerText = draft;
      setIsDraftBoxOpen(false);
      setPlaceHolderVisible(false);
    }
  };

  const CharacterCounter = ({ charCount, maxCharCount }) => {
    return (
      <Box position="absolute" bottom="0" right="0" padding="5px">
        <Text fontSize="sm" color={charCount >= maxCharCount ? "red" : "gray"}>
          {charCount}/{maxCharCount}
        </Text>
      </Box>
    );
  };

  const limitInputChars = () => {
    const currentValue = textareaRef.current.innerText;
    setCharCount(currentValue.length);
  };

  const handleInput = () => {
    limitInputChars();
    adjustTextareaHeight();
  };

  const checkMention = async function () {
    await debouncedTyping();
    if (usedSuggestedDraft === "editing") setUsedSuggestedDraft("edited");
    if (replyType !== "internalNote") return;

    const currentValue = textareaRef.current.innerText;
    const lastAtPos = currentValue.lastIndexOf("@");

    if (lastAtPos !== -1 && /\@[a-zA-Z ]{1,14}$/.test(currentValue)) {
      const searchQuery = currentValue.substring(lastAtPos + 1).toLowerCase();
      const matchingStaff = staff
        .filter((s) => {
          const nameParts = s.profile.name.toLowerCase().split(" ");
          return nameParts.some((part) => part.startsWith(searchQuery));
        })
        .filter((s) => !mentionedStaff.find((ss) => ss._id === s._id))
        .slice(0, 3);
      if (!_.isEqual(matchingStaff, filteredStaff)) {
        setFilteredStaff(matchingStaff);
      }
    } else {
      setFilteredStaff([]);
    }
  };

  const handleDeleteAttachment = (index) => {
    setAttachedFiles((currentFiles) => currentFiles.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    const uploadedFiles = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append("file", file, encodeURIComponent(file.name));
      try {
        const response = await cqAPI.post("/upload", formData);
        if (response.type === "ok") {
          uploadedFiles.push({
            name: file.name || "Attachment",
            url: response.url,
            mimetype: file.type,
          });
        } else {
          toast({
            title: "Error uploading file.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          break;
        }
      } catch (error) {
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        break;
      }
    }
    if (uploadedFiles.length > 0) {
      toast({
        title: "File(s) uploaded successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      setAttachedFiles((currentFiles) => [...currentFiles, ...uploadedFiles]);
      setFileInputKey(Date.now());
    }
  };

  const deleteStaff = (member) => {
    textareaRef.current.innerHTML = textareaRef.current.innerHTML.replace(
      `<b>${member.profile.name}</b>`,
      ""
    );
    setCursorAfterHtmlInsert(textareaRef.current);
    setMentionedStaff([...mentionedStaff.filter((s) => s._id !== member._id)]);
  };

  const handleDraftButtonClick = async () => {
    if (isDraftBoxOpen) {
      if (usedSuggestedDraft === "loaded") setUsedSuggestedDraft("ignored");
      setIsDraftBoxOpen(false);
    } else if (!!draft) {
      if (placeHolderVisible) setUsedSuggestedDraft("loaded");
      setIsDraftBoxOpen(true);
    }
  };

  const handleFormButtonClick = async () => {
    const [response, historyTemaplate] = await Promise.all([
      cqAPI.get("/settings/esign?active_only=1"),
      cqAPI.get("/settings/history?active_only=1")
    ])
    if (response.type === "ok") {
      const sortedForms = [...historyTemaplate.data, ...response.data].sort((a, b) => a?.name?.localeCompare(b?.name));
      setForms(sortedForms);
      onFormModalOpen();
    } else {
      toast({
        title: "Error fetching forms.",
        description: response.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handleFileButtonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSubmit = async () => {
    if (!textareaRef.current.innerText && !draft) {
      toast({
        title: "The Message Cannot be Empty!",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    setDisableSendButton(true);

    const msgBody = textareaRef.current.innerText
      ? textareaRef.current.innerText
      : draft
      ? draft
      : "";

    // Only include non-empty replacements; omit entirely if none
    const nonEmptyReplacements =
      attachedFormReplacements &&
      Object.fromEntries(
        Object.entries(attachedFormReplacements).filter(
          ([, v]) => typeof v === "string" && v.trim().length > 0
        )
      );
    const includeReplacements =
      nonEmptyReplacements && Object.keys(nonEmptyReplacements).length > 0;

    const response = await cqAPI.post("/messages", {
      body: msgBody,
      to: uid,
      ...(translationLangCode ? { translate_to: translationLangCode } : {}),
      is_internal_note: replyType === "internalNote",
      ...(replyType === "internalNote" ? {} : { type: messageType.join("") }),
      attachments: attachedFiles,
      ...(replyType === "internalNote"
        ? { mentions: mentionedStaff.map((v) => v._id) }
        : {}),
      related_to: userInfo.user_id,
      ...(attachedPayRequest && attachedPayRequest.amount
        ? { payrequest: attachedPayRequest }
        : {}),
      ...(attachedForm
        ? attachedForm?.data?.length? {
          formrequest: {
            attachistory : true,
            template_id: attachedForm._id,
          },
        } : {
            formrequest: {
              template_id: attachedForm._id,
              title: attachedForm.name,
              ...(includeReplacements ? { replacements: nonEmptyReplacements } : {}),
            },
          }
        : {}),
    });

    setDisableSendButton(false);

    if (response.type === "ok") {
      const draftStatus =
        usedSuggestedDraft === "edited"
          ? "edited"
          : usedSuggestedDraft === "loaded" || usedSuggestedDraft === "editing"
          ? "as-is"
          : usedSuggestedDraft
          ? "ignored"
          : "not-available";

      sendEvent("message-sent", {
        type: messageType.join("").toLowerCase(),
        direction: "outgoing",
        is_patient: isPatient,
        used_suggested_draft: draftStatus,
      });

      if (usedSuggestedDraft && replyType !== "internalNote") {
        await cqAPI.post("/draft-feedback", {
          uid,
          status: draftStatus,
          body: msgBody,
        });
      }

      // Clear states
      setCharCount(0);
      setAttachedFiles([]);
      setPayRequest({});
      setAttachedPayRequest({});
      setAttachedForm(null);
      setAttachedFormReplacements(null);
      textareaRef.current.innerText = "";
      setMentionedStaff([]);
      setDraft("");
      setIsDraftBoxOpen(false);

      const url = new URL(window.location.href);
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
    } else if (response.msg) {
      toast({
        title: "Error sending the message.",
        description: response.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const DraftMessageBox = ({
    placeHolderVisible,
    draft,
    isDraftBoxOpen,
    colorMode,
    isPatient,
    handleDraftBoxClick,
    handleDraftCloseClick,
  }) => {
    return (
      <>
        {!isPatient && placeHolderVisible && !!draft && isDraftBoxOpen && (
          <Flex borderRadius="md" position="absolute" mr={10} direction="column" width="auto">
            <Box
              borderRadius="md"
              bg={colorMode === "dark" ? "gray.600" : "gray.100"}
              p={3}
              overflowY="auto"
              maxHeight="100px"
            >
              <Text
                color={colorMode === "dark" ? "#E2F3F3" : "gray.600"}
                fontSize="sm"
                align="left"
                paddingTop="10px"
                paddingLeft="10px"
                paddingRight="10px"
                paddingBottom="5px"
                _hover={{
                  bg: colorMode === "dark" ? "gray.500" : "gray.200",
                  cursor: "pointer",
                }}
                onClick={handleDraftBoxClick}
              >
                {draft}
              </Text>
              <CloseButton
                size="sm"
                position="absolute"
                top="2"
                right="4"
                color={colorMode === "dark" ? "whiteAlpha.800" : "gray.600"}
                onClick={handleDraftCloseClick}
              />
            </Box>
          </Flex>
        )}
      </>
    );
  };

  const replacementKeys = React.useMemo(
    () => attachedForm?.replacements ?? [],
    [attachedForm]
  );

  return (
    <>
      <Box
        w="100%"
        className="input-area"
        bg={
          replyType === "internalNote"
            ? "orange.100"
            : colorMode === "dark"
            ? "#212330"
            : "#FFFFFF"
        }
        color={
          replyType === "internalNote"
            ? "#000000"
            : colorMode === "light"
            ? "#000000"
            : "#ffffff"
        }
      >
        <Flex direction="column" flex="1">
          <VStack spacing={3} align="stretch">
            {/* Hidden file input */}
            <Input
              ref={fileInputRef}
              key={fileInputKey}
              type="file"
              onChange={handleFileUpload}
              hidden
            />

            {/* Tools row */}
            <HStack
              className="stack_custom_style"
              style={{ zIndex: 99999, width: "100%", justifyContent: "space-between" }}
            >
              {/* Left side */}
              <HStack className="chatTools">
                <>
                  <IconButton
                    icon={<AttachmentIcon />}
                    id={"attach-icon"}
                    onClick={handleFileButtonClick}
                    isDisabled={disableSendButton}
                    variant="none"
                    bg={iconBGColor}
                    className={replyType === "internalNote" ? "attachmentIcon" : ""}
                    mr={1}
                  />
                  {replyType !== "internalNote" && !isPatient && !attachedForm && (
                    <IconButton
                      id={"form-icon"}
                      icon={<FontAwesomeIcon icon={faFileSignature} />}
                      isDisabled={disableSendButton}
                      onClick={handleFormButtonClick}
                      variant="none"
                      mr={1}
                      bg={iconBGColor}
                    />
                  )}
                  {replyType !== "internalNote" && !isPatient && attachedForm && (
                    <Flex position="relative" ml={0}>
                      <Tooltip
                        label={
                          attachedFormReplacements
                            ? `${attachedForm.name} (with replacements)`
                            : attachedForm.name
                        }
                        hasArrow
                      >
                        <IconButton
                          icon={<FontAwesomeIcon icon={faFileSignature} />}
                          variant="outline"
                          size="md"
                          onClick={() => {
                            if (Array.isArray(attachedForm.replacements) && attachedForm.replacements.length > 0) {
                              const seeded = attachedForm.replacements.reduce((acc, key) => {
                                acc[key] = attachedFormReplacements?.[key] ?? "";
                                return acc;
                              }, {});
                              setReplacementValues(seeded);
                              onReplacementsOpen();
                            }
                          }}
                        />
                      </Tooltip>
                      {Array.isArray(attachedForm.replacements) &&
                        attachedForm.replacements.length > 0 && (
                          <Tooltip label="Edit replacements" hasArrow>
                            <IconButton
                              aria-label="Edit replacements"
                              icon={<EditIcon />}
                              size="xs"
                              variant="ghost"
                              onClick={() => {
                                const seeded = attachedForm.replacements.reduce((acc, key) => {
                                  acc[key] = attachedFormReplacements?.[key] ?? "";
                                  return acc;
                                }, {});
                                setReplacementValues(seeded);
                                onReplacementsOpen();
                              }}
                              style={{ position: "absolute", left: "-8px", top: "-8px" }}
                            />
                          </Tooltip>
                        )}
                      <Box position="absolute" right="-8px" top="-8px">
                        <IconButton
                          icon={<DeleteIcon />}
                          size="xs"
                          onClick={() => {
                            setAttachedForm(null);
                            setAttachedFormReplacements(null);
                          }}
                          aria-label="Remove form attachment"
                          variant="ghost"
                          style={{
                            filter:
                              "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                          }}
                        />
                      </Box>
                    </Flex>
                  )}
                  {replyType !== "internalNote" &&
                    !isPatient &&
                    isStripeConnected &&
                    !attachedPayRequest.amount && (
                      <IconButton
                        id={"payment-icon"}
                        icon={<FontAwesomeIcon icon={faDollarSign} />}
                        bg={iconBGColor}
                        isDisabled={disableSendButton}
                        onClick={() =>
                          creditCardModalManager.open({
                            payButtonText: "Attach",
                            showSourceOnlyForInstallment: true,
                            userProfileName: userInfo.name,
                            initialSource: {
                              type: userInfo?.source?.type,
                              last4: userInfo?.source?.last4,
                            },
                            _uid: uid,
                            onSubmit: (params) => {
                              setPayRequest({
                                ...params,
                                description: params.desc,
                              });
                              setAttachedPayRequest({
                                ...params,
                                description: params.desc,
                              });
                              return true;
                            },
                            hideFSA: true,
                            onCardChange: ({ type, last4 }) =>
                              setUserInfo({
                                ...userInfo,
                                source: {
                                  type,
                                  last4,
                                },
                              }),
                          })
                        }
                        variant="none"
                        mr={1}
                      />
                    )}
                  {replyType !== "internalNote" &&
                    isStripeConnected &&
                    attachedPayRequest.amount && (
                      <Flex position="relative" ml={0}>
                        <Tooltip label={`$${attachedPayRequest.amount} Requested`} hasArrow>
                          <IconButton icon={<FontAwesomeIcon icon={faDollarSign} />} variant="outline" size="md" />
                        </Tooltip>
                        <Box position="absolute" right="-8px" top="-8px">
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            onClick={() => {
                              setAttachedPayRequest({});
                            }}
                            aria-label="Remove attachment"
                            variant="ghost"
                            style={{
                              filter:
                                "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                            }}
                          />
                        </Box>
                      </Flex>
                    )}
                  {replyType !== "internalNote" &&
                    !isPatient && (
                    <IconButton
                        id={"template-icon"}
                        icon={<FontAwesomeIcon icon={faBookmark}/>}
                        bg={iconBGColor}
                        isDisabled={disableSendButton}
                        onClick={() =>{onMessageTemplatesOpen()}}
                        variant="none"
                        mr={1}
                      />
                    )}
                </>

                {/* Staff mention area */}
                <Stack direction="row" spacing={4} mr={2}>
                  <Flex className="staff-selector">
                    {mentionedStaff.length > 0 &&
                      mentionedStaff.map((member) => (
                        <div key={member._id} className="staff-member">
                          {member.profile.name}
                          <IconButton
                            icon={<DeleteIcon />}
                            onClick={() => deleteStaff(member)}
                            variant="none"
                            size="xs"
                            style={{
                              filter:
                                "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                            }}
                          />
                        </div>
                      ))}
                    {filteredStaff.length > 0 &&
                      filteredStaff.map((member) => (
                        <div
                          key={member._id}
                          onClick={() => insertStaffNameAtCursor(member)}
                          className="staff-member"
                        >
                          {member.profile.name}
                        </div>
                      ))}
                  </Flex>
                </Stack>

                {/* Attached files */}
                {attachedFiles && (
                  <Flex justify="flex-end">
                    {attachedFiles.map((attachedFile, index) => (
                      <Flex key={index} position="relative" ml={index > 0 ? 2 : 0}>
                        <Tooltip label={attachedFile.name} hasArrow>
                          <IconButton icon={<AttachmentIcon />} variant="outline" size="md" />
                        </Tooltip>
                        <Box position="absolute" right="-8px" top="-8px">
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            onClick={() => handleDeleteAttachment(index)}
                            aria-label="Remove attachment"
                            variant="ghost"
                            style={{
                              filter:
                                "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                            }}
                          />
                        </Box>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </HStack>

              {/* Right side (Translate + Suggested answer) */}
              <HStack spacing="20px">
                {(!isPatient && replyType !== "internalNote" && userInfo.language) && (
                  <Button
                    leftIcon={translateEnabled ? <CheckIcon color="#B674BC" /> : undefined}
                    minWidth="170"
                    bg={iconBGColor}
                    border={!!draft && isDraftBoxOpen && "1px solid #8E4A97"}
                    onClick={() => {
                      setTranslateEnabled((prev) => !prev);
                      setTranslationLangCode(!translateEnabled ? userInfo.language.code : "");
                    }}
                    sx={{
                      backgroundColor: colorMode === "dark" ? "#2C2F40" : "#F9F9FB",
                      ":hover": {
                        backgroundColor: colorMode === "dark" ? "#4a5568" : "gray.200",
                      },
                    }}
                  >
                    <Flex align="center" justify="flex-start" width="100%" mb={1} mt={1}>
                      <Text fontSize="sm" color={colorMode === "dark" ? "whiteAlpha.800" : "gray.500"}>
                        {`Translate to ${userInfo.language.name}`}
                      </Text>
                    </Flex>
                  </Button>
                )}

                {!isPatient && replyType !== "internalNote" && (
                  <IconButton
                    id={"suggest-answer"}
                    className="sparkleIcon"
                    minWidth="170"
                    bg={iconBGColor}
                    border={!!draft && isDraftBoxOpen && "1px solid #8E4A97"}
                    onClick={handleDraftButtonClick}
                    sx={{
                      backgroundColor: colorMode === "dark" ? "#2C2F40" : "#F9F9FB",
                      ":hover": { backgroundColor: colorMode === "dark" ? "#4a5568" : "gray.200" },
                    }}
                    isDisabled={!draft}
                    icon={
                      <Flex align="center" justify="flex-start" width="100%" mb={1} mt={1}>
                        <Icon as={RiSparkling2Fill} boxSize={4} ml={2} mr={2} color="#B674BC" />
                        {!isMobile && (
                          <Text fontSize="sm" color={colorMode === "dark" ? "whiteAlpha.800" : "gray.500"}>
                            Suggested answer
                          </Text>
                        )}
                      </Flex>
                    }
                  />
                )}
              </HStack>
            </HStack>

            <div style={{ width: "100%" }} id="input-box">
              <Flex direction="column" position="relative">
                <Flex direction="column" w="full" position="relative">
                  <div
                    contentEditable
                    ref={textareaRef}
                    className="chatareatext"
                    id="text-area"
                    name="question"
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onKeyUp={checkMention}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  {placeHolderVisible && (!draft || !isDraftBoxOpen) && (
                    <div onClick={handlePlaceHolderClick} style={{ position: "absolute", top: "0px", left: "0px" }}>
                      Type your message...
                    </div>
                  )}

                  {replyType !== "internalNote" && !isPatient && (
                    <DraftMessageBox
                      placeHolderVisible={placeHolderVisible}
                      isDraftBoxOpen={isDraftBoxOpen}
                      draft={draft}
                      colorMode={colorMode}
                      isPatient={isPatient}
                      handleDraftBoxClick={handleDraftBoxClick}
                      handleDraftCloseClick={handleDraftCloseClick}
                    />
                  )}

                  <Flex align="center" className="text-area-button">
                    <Stack direction="row" spacing={isMobile ? 1 : 4} mr={2}>
                      {window.location.pathname.startsWith("/inbox") && !isPatient && (
                        <Link id={"reply"} onClick={() => setReplyType("reply")} color={replyType === "reply" ? "blue.500" : "gray.500"}>
                          Reply
                        </Link>
                      )}
                      {!isPatient && (
                        <Link
                          id={"internal-note"}
                          onClick={() => setReplyType("internalNote")}
                          style={{ minWidth: "100px" }}
                          color={replyType === "internalNote" ? "blue.500" : "gray.500"}
                        >
                          Internal Note
                        </Link>
                      )}
                      {replyType !== "internalNote" && !isPatient && (
                        <>
                          <Box width="1px" bg="gray.200" mx={2} />
                          <CheckboxGroup colorScheme="blue" value={messageType} onChange={setMessageType}>
                            <Checkbox value="email" disabled={!userInfo.email} id={"email-response"}>
                              Email
                            </Checkbox>
                            <Checkbox value="sms" id={"sms-response"} disabled={!userInfo.phone} style={{ zIndex: "1" }}>
                              SMS
                            </Checkbox>
                            {typing && (
                              <Flex paddingTop="10px">
                                <Box className="throbbing-dot" mr={1}></Box>
                                <Box className="throbbing-dot" mr={1} style={{ animationDelay: "0.3s" }}></Box>
                                <Box className="throbbing-dot" style={{ animationDelay: "0.6s" }}></Box>
                              </Flex>
                            )}
                          </CheckboxGroup>
                        </>
                      )}
                    </Stack>
                    {!isPatient && replyType === "internalNote" && (
                      <Flex
                        flex="1"
                        justify="center"
                      >
                        <Text
                          color="gray.500"
                        >
                          This message is not sent to the patient
                        </Text>
                      </Flex>
                    )}
                    <Button variant="sendMessage" onClick={handleSubmit} isDisabled={disableSendButton} ml={"auto"} id="send-button">
                      <ArrowForwardIcon />
                    </Button>
                  </Flex>
                </Flex>
                <Box
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  style={{ position: "absolute", right: "55px", bottom: "-15px" }}
                  mt="32px"
                  mb="0px"
                >
                  {replyType !== "internalNote" && messageType.includes("sms") && (
                    <CharacterCounter charCount={charCount} maxCharCount={MAX_CHAR_COUNT} />
                  )}
                </Box>
              </Flex>
            </div>
          </VStack>
        </Flex>
      </Box>

      {/* FORM MODAL FOR ATTACHING A FORM */}
      <Modal isOpen={isFormModalOpen} onClose={onFormModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select a Form</ModalHeader>
          <ModalCloseButton id={"close-diag"} />
        <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Forms</FormLabel>
              <Select
                id={"select-form"}
                placeholder="Select a form"
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
              >
                {forms?.map((form) => (
                  <option key={form._id} value={form._id}>
                    {form.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button id={"diag-attach"} colorScheme="blue" mr={3} onClick={handleAttachForm}>
              Attach
            </Button>
            <Button id={"diag-cancel"} onClick={onFormModalClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* REPLACEMENTS MODAL */}
      <Modal
        isOpen={isReplacementsOpen}
        onClose={() => {
          // Treat close as cancel: form should NOT be added
          setAttachedForm(null);
          setAttachedFormReplacements(null);
          onReplacementsClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Fill in placeholders</ModalHeader>

          <ModalCloseButton
            onClick={() => {
              setAttachedForm(null);
              setAttachedFormReplacements(null);
              onReplacementsClose();
            }}
          />

          <ModalBody pb={6}>
            {/* faint hint text */}
            <Text fontSize="sm" color="gray.500" mb={3}>
              Tip: You can press Enter to add multiple lines. The field will grow as you type. You can also leave these blank.
            </Text>

            {replacementKeys?.length ? (
              <VStack align="stretch" spacing={4}>
                {replacementKeys.map((key) => (
                  <FormControl key={key}>
                    <FormLabel>{key}</FormLabel>
                    <AutoGrowUncontrolledTextarea
                      name={key}
                      initialValue={replacementValues[key] ?? ""}
                      placeholder={`Enter ${key}`}
                      onValueChange={(val) =>
                        setReplacementValues((prev) =>
                          prev[key] === val ? prev : { ...prev, [key]: val }
                        )
                      }
                    />
                  </FormControl>
                ))}
              </VStack>
            ) : (
              <Text>No replacements for this form.</Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                // Save only non-empty fields; if all empty, omit replacements in payload
                const filtered = Object.fromEntries(
                  Object.entries(replacementValues).filter(
                    ([, v]) => typeof v === "string" && v.trim().length > 0
                  )
                );
                setAttachedFormReplacements(
                  Object.keys(filtered).length > 0 ? filtered : null
                );
                onReplacementsClose();
                toast({
                  title: "Form attached",
                  description:
                    Object.keys(filtered).length > 0
                      ? "Your replacement values have been saved."
                      : "Form attached without replacement values.",
                  status: "success",
                  duration: 4000,
                  isClosable: true,
                });
              }}
            >
              Attach
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                // explicit Cancel: do NOT attach the form
                setAttachedForm(null);
                setAttachedFormReplacements(null);
                onReplacementsClose();
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {!isPatient &&
        <MessagesTemplatesModal isOpen={isMessageTemplatesOpen} onClose={onMessageTemplatesClose}
                                onOpen={onMessageTemplatesOpen} onDone={replaceTextWithTemplate} userInfo={userInfo}/>
      }
    </>
  );
}

export default SendArea;
